import _, { compact, includes, map } from "lodash/fp";
import { Tooltip } from "@material-ui/core";
import InfoIcon from "@mui/icons-material/Info";
import React from "react";
import { getJustTheDateInHk } from "./dateUtils";
import { ADMIN } from "../consts/roles";
import { isNotEmpty } from "./funcUtils";

const getHumanReadableReason = (
  conflictingJurisdiction,
  affiliatedSchool,
  notAccepted,
  participationIsOnlyWritten,
  noLongerAvailableToActAsAnArbitrator,
  overRoundCount,
  alreadyPaired,
  notAvailable,
  moreThanThreePairingsToday,
  alreadyArbitratingThisPairing,
  isCoachingOneOfTheTeams,
  coachesTeamThatFacesOneOfTheseTeams,
  coachesTeamAtTheSameTimeAsThisPairing,
) => {
  let reason = [];
  if (conflictingJurisdiction) {
    reason.push("Conflicting jurisdiction");
  }
  if (affiliatedSchool) {
    reason.push("Affiliated school");
  }
  if (notAccepted) {
    reason.push("Not accepted");
  }
  if (participationIsOnlyWritten) {
    reason.push("Only available for written");
  }
  if (noLongerAvailableToActAsAnArbitrator) {
    reason.push(
      `Switched participation to 'no longer available to act as an arbitrator'`,
    );
  }
  if (overRoundCount) {
    reason.push("Over their selected round count");
  }
  if (alreadyPaired) {
    reason.push("Already paired at this time");
  }
  if (notAvailable) {
    reason.push("Unavailable at this time");
  }
  if (moreThanThreePairingsToday) {
    reason.push("Paired more than 3 times today");
  }
  if (alreadyArbitratingThisPairing) {
    reason.push("Already arbitrating this pairing");
  }
  if (isCoachingOneOfTheTeams) {
    reason.push("Is a coach of one of these teams");
  }
  if (coachesTeamThatFacesOneOfTheseTeams) {
    reason.push("Is a coach of an opponent of one of these teams");
  }
  if (coachesTeamAtTheSameTimeAsThisPairing) {
    reason.push("Is a coach of a team competing at this same time");
  }

  return _.join(", ", reason);
};
export const hasConflict = (
  claimant,
  respondent,
  arbitrator,
  schedule,
  pairingTime,
) => {
  if (!arbitrator) return false;

  const conflictingJurisdictionNames = map(
    (j) => j.name,
    arbitrator?.conflictingJurisdictions,
  );
  const affiliatedSchoolNames = map(
    (j) => j.name,
    arbitrator?.affiliatedSchools,
  );

  const conflictingJurisdiction =
    includes(claimant?.jurisdiction, conflictingJurisdictionNames) ||
    includes(respondent?.jurisdiction, conflictingJurisdictionNames);
  const affiliatedSchool =
    includes(claimant?.school, affiliatedSchoolNames) ||
    includes(respondent?.school, affiliatedSchoolNames);

  const notAccepted = arbitrator?.status !== "accepted";
  const participationIsOnlyWritten =
    arbitrator?.participation ===
    "I am available ONLY to rank written memoranda";
  const noLongerAvailableToActAsAnArbitrator =
    arbitrator?.participation === "I am no longer able to act as an arbitrator";
  const overRoundCount = arbitrator?.isOverRoundCount === true;
  const alreadyPaired = arbitrator.isAlreadyPaired === true;
  const alreadyArbitratingThisPairing =
    arbitrator.isAlreadyArbitratingThisPairing === true;
  const notAvailable = arbitrator.isAvailable === false;
  const moreThanThreePairingsToday =
    arbitrator.isPairedMoreThanThreeTimesToday === true;

  const coachesTeamMatch = _.filter(
    (teamId) => teamId === claimant?.id || teamId === respondent?.id,
    arbitrator.coachesTeam,
  );

  const respondentsTheyFace = _.map(
    "respondentId",
    _.filter(
      (pairing) => arbitrator.coachesTeam?.includes(pairing.claimantId),
      schedule,
    ),
  );

  const claimantsTheyFace = _.map(
    "claimantId",
    _.filter(
      (pairing) => arbitrator.coachesTeam?.includes(pairing.respondentId),
      schedule,
    ),
  );

  const coachesTeamThatFacesOneOfTheseTeams =
    !arbitrator.coachesTeam?.includes(claimant?.id) &&
    !arbitrator.coachesTeam?.includes(respondent?.id) &&
    isNotEmpty(
      _.intersection(_.concat(respondentsTheyFace, claimantsTheyFace), [
        claimant?.id,
        respondent?.id,
      ]),
    );

  const timesTheirTeamsAreCompeting = _.map(
    "pairingTime",
    _.filter(
      (pairing) =>
        arbitrator.coachesTeam?.includes(pairing.claimant?.id) ||
        arbitrator.coachesTeam?.includes(pairing.respondent?.id),
      schedule,
    ),
  );

  const coachesTeamAtTheSameTimeAsThisPairing =
    timesTheirTeamsAreCompeting.includes(pairingTime) &&
    !arbitrator.coachesTeam?.includes(claimant?.id) &&
    !arbitrator.coachesTeam?.includes(respondent?.id);

  const isCoachingOneOfTheTeams = isNotEmpty(coachesTeamMatch);
  const isConflicted =
    conflictingJurisdiction ||
    affiliatedSchool ||
    notAccepted ||
    participationIsOnlyWritten ||
    noLongerAvailableToActAsAnArbitrator ||
    overRoundCount ||
    alreadyPaired ||
    alreadyArbitratingThisPairing ||
    notAvailable ||
    moreThanThreePairingsToday ||
    isCoachingOneOfTheTeams ||
    coachesTeamThatFacesOneOfTheseTeams ||
    coachesTeamAtTheSameTimeAsThisPairing;

  const reason = getHumanReadableReason(
    conflictingJurisdiction,
    affiliatedSchool,
    notAccepted,
    participationIsOnlyWritten,
    noLongerAvailableToActAsAnArbitrator,
    overRoundCount,
    alreadyPaired,
    notAvailable,
    moreThanThreePairingsToday,
    alreadyArbitratingThisPairing,
    isCoachingOneOfTheTeams,
    coachesTeamThatFacesOneOfTheseTeams,
    coachesTeamAtTheSameTimeAsThisPairing,
  );
  return { isConflicted, reason };
};

export const buildArbitratorOptionList = (
  claimant,
  respondent,
  allArbitrators,
  pairingTime,
  schedule,
  pairingId,
) => {
  let dailyCountMap = [];
  if (pairingTime && schedule) {
    dailyCountMap = buildDailyPairingCountMap(pairingTime, schedule);
  }
  const arbs = compact(
    map((a) => {
      if (a?.status === "accepted") {
        const augmentedArb = augmentArbitrator(
          a,
          pairingTime,
          schedule,
          pairingId,
          dailyCountMap,
        );
        const { isConflicted, reason } = hasConflict(
          claimant,
          respondent,
          augmentedArb,
          schedule,
          pairingTime,
        );
        return {
          id: a.id,
          name: `${a.firstName} ${a.lastName || ""}`,
          isConflicted,
          reason,
          ...augmentedArb,
        };
      }
      return null;
    }, allArbitrators),
  );

  return [{ id: -1, name: "--Select Arbitrator--" }, ...arbs];
};

export const getArbitratorConflictGridCell = (
  claimant,
  respondent,
  arbitrator,
  pairingTime,
  schedule,
  pairingId,
  userRoles,
) => {
  // Check if not an admin. e.g. scorer
  if (!includes(ADMIN, userRoles)) {
    return (
      <span>
        {`${arbitrator?.firstName ?? ""} ${arbitrator?.lastName ?? ""}`.trim()}
      </span>
    );
  }

  let dailyCountMap = [];
  if (pairingTime && schedule) {
    dailyCountMap = buildDailyPairingCountMap(pairingTime, schedule);
  }
  const augmentedArbitrator = augmentArbitrator(
    arbitrator,
    pairingTime,
    schedule,
    pairingId,
    dailyCountMap,
  );
  const { isConflicted, reason } = hasConflict(
    claimant,
    respondent,
    augmentedArbitrator,
    schedule,
    pairingTime,
  );
  const arbitratorName =
    `${augmentedArbitrator?.firstName ?? ""} ${augmentedArbitrator?.lastName ?? ""}`.trim();

  const hasWarning =
    augmentedArbitrator.isAtRoundCount ||
    augmentedArbitrator.isPairedThreeTimesToday;
  const hasAny = isConflicted || hasWarning;

  // Build combined reason - only include warnings if there's no conflict
  const warnings = [
    augmentedArbitrator.isAtRoundCount ? "At round count" : null,
    augmentedArbitrator.isPairedThreeTimesToday ? "Paired 3 times today" : null,
  ];
  const reasons = isConflicted ? reason : _.join(". ", _.compact(warnings));

  return (
    arbitrator?.id && (
      <span
        className={
          isConflicted
            ? "arbitrator-conflict"
            : hasWarning
              ? "arbitrator-warning"
              : ""
        }
      >
        {hasAny ? (
          <Tooltip title={reasons}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              {arbitratorName}
              <InfoIcon sx={{ fontSize: 14, marginLeft: "2px" }} />
            </span>
          </Tooltip>
        ) : (
          arbitratorName
        )}
      </span>
    )
  );
};

const arbitratorRoundCount = (arbitrator, schedule) => {
  if (!arbitrator) return false;
  const id = arbitrator.id;
  return _.sumBy(
    (s) =>
      s.chairArbitratorId === id ||
      s.secondArbitratorId === id ||
      s.thirdArbitratorId === id
        ? 1
        : 0,
    schedule,
  );
};

const isArbitratorAvailable = (arbitrator, pairingTime) => {
  if (!arbitrator) return false;
  const availableTimes = _.map((s) => s?.available, arbitrator.schedule);
  return availableTimes?.includes(pairingTime);
};
const isAlreadyPairedForThisTimeSlot = (
  arbitrator,
  pairingTime,
  pairingId,
  schedule,
) => {
  if (!arbitrator) return false;
  const otherPairingsAtThisTime = _.filter(
    (s) => s.pairingTime === pairingTime && s.id !== parseInt(pairingId),
    schedule,
  );

  const id = arbitrator.id;
  const count = _.sumBy(
    (s) =>
      s.chairArbitratorId === id ||
      s.secondArbitratorId === id ||
      s.thirdArbitratorId === id
        ? 1
        : 0,
    otherPairingsAtThisTime,
  );
  return count > 0;
};

const arbitratingThisPairingMoreThanOnce = (
  arbitrator,
  pairingId,
  schedule,
) => {
  if (!arbitrator) return false;
  const id = arbitrator.id;

  const thisPairing = _.head(
    _.filter((s) => s.id === parseInt(pairingId), schedule),
  );

  // A Set enforces unique items, so if we try to add all the ids and we don't get a size of 3 then that means
  // at least one of the ids is duplicated, meaning the arbitrator is paired more than once
  // Only add the id to the set if is the arbitrators id so we ensure we are only looking for duplicates of this arbitrator
  const set = new Set([
    thisPairing?.chairArbitratorId === id ? id : Math.random(),
    thisPairing?.secondArbitratorId === id ? id : Math.random(),
    thisPairing?.thirdArbitratorId === id ? id : Math.random(),
  ]);

  return set?.size < 3;
};

const alreadySelectedToArbitrateThisPairing = (
  arbitrator,
  pairingId,
  schedule,
) => {
  if (!arbitrator) return false;
  const id = arbitrator.id;

  const thisPairing = _.head(
    _.filter((s) => s.id === parseInt(pairingId), schedule),
  );

  if (
    thisPairing?.chairArbitratorId === id ||
    thisPairing?.secondArbitratorId === id ||
    thisPairing?.thirdArbitratorId === id
  ) {
    return true;
  }
};

const buildDailyPairingCountMap = (pairingTime, schedule) => {
  const thePairingTime = getJustTheDateInHk(pairingTime);
  const otherPairingsToday = _.filter(
    (s) => getJustTheDateInHk(s.pairingTime) === thePairingTime,
    schedule,
  );

  const countMap = {};
  otherPairingsToday.forEach((s) => {
    [s.chairArbitratorId, s.secondArbitratorId, s.thirdArbitratorId].forEach(
      (id) => {
        if (id) {
          countMap[id] = (countMap[id] || 0) + 1;
        }
      },
    );
  });

  return countMap;
};

const arbitratorPairingTodayCount = (arbitrator, dailyCountMap) => {
  if (!arbitrator) return false;
  return dailyCountMap[arbitrator.id] || 0;
};

const buildCoachList = (arbitrator, schedule) => {
  const arbitratorPairingMatch = _.find(
    (p) =>
      p.chairArbitrator?.id === arbitrator.id ||
      p.secondArbitrator?.id === arbitrator.id ||
      p.thirdArbitrator?.id === arbitrator.id ||
      p.firstReserveArbitrator?.id === arbitrator.id ||
      p.secondReserveArbitrator?.id === arbitrator.id ||
      p.thirdReserveArbitrator?.id === arbitrator.id,
    schedule,
  );

  if (arbitratorPairingMatch) {
    if (arbitratorPairingMatch.chairArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.chairArbitrator?.coachesTeam;
    }
    if (arbitratorPairingMatch.secondArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.secondArbitrator?.coachesTeam;
    }
    if (arbitratorPairingMatch.thirdArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.thirdArbitrator?.coachesTeam;
    }
    if (arbitratorPairingMatch.firstReserveArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.firstReserveArbitrator?.coachesTeam;
    }
    if (arbitratorPairingMatch.secondReserveArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.secondReserveArbitrator?.coachesTeam;
    }
    if (arbitratorPairingMatch.thirdReserveArbitrator.id === arbitrator.id) {
      return arbitratorPairingMatch.thirdReserveArbitrator?.coachesTeam;
    }
  }
  return [];
};

export const augmentArbitrator = (
  arbitrator,
  pairingTime,
  schedule,
  pairingId,
  dailyCountMap,
) => {
  const roundCount = arbitratorRoundCount(arbitrator, schedule);
  const isOverRoundCount = arbitrator?.roundsYouCanArbitrate < roundCount;
  const isAtRoundCount = arbitrator?.roundsYouCanArbitrate === roundCount;

  return {
    ...arbitrator,
    isOverRoundCount,
    isAtRoundCount,
    roundCount,
    isAvailable: isArbitratorAvailable(arbitrator, pairingTime),
    isAlreadyPaired: isAlreadyPairedForThisTimeSlot(
      arbitrator,
      pairingTime,
      pairingId,
      schedule,
    ),
    isPairedMoreThanThreeTimesToday:
      arbitratorPairingTodayCount(arbitrator, dailyCountMap) > 3,
    isPairedThreeTimesToday:
      arbitratorPairingTodayCount(arbitrator, dailyCountMap) === 3,
    isAlreadyArbitratingThisPairing: arbitratingThisPairingMoreThanOnce(
      arbitrator,
      pairingId,
      schedule,
    ),
    coachesTeam: buildCoachList(arbitrator, schedule),
  };
};
